/**
 * Component JSON detail editor (Sprint 4.3.4, PROD-653).
 *
 * Renders the JSON for a single care-flow component instance. Two
 * editing surfaces, both saving through the same Zod-gated path:
 *
 *   - Form mode (default): typed inputs for the component's
 *     `id` / `label` / `version` / `lastModified`.
 *   - Raw JSON mode: a labelled textarea carrying the JSON-encoded
 *     candidate. The user can paste / edit raw JSON; the editor parses
 *     and validates against the matching Zod schema before saving.
 *
 * Save runs through `validateComponentJson(folder, candidate)`. On
 * success the candidate is committed via the prop callback; on failure
 * the structured error list renders inline so the user can correct.
 *
 * Version-history sidebar: shows the previous N versions (caller-
 * provided) with a textual diff of the JSON.
 *
 * HC5: zero em / en dashes.
 * HC6 a11y: form inputs all carry `<label htmlFor>`; mode toggle is a
 * `role="radiogroup"` with two radio buttons.
 */
import { useMemo, useState } from 'react';
import {
  CARE_FLOW_FOLDER_LABELS,
  isSharedFolder,
  type CareFlowComponentJson,
  type CareFlowFolder,
} from '../../services/care-flow-components/types';
import {
  validateComponentJson,
  type ValidationResult,
} from '../../services/care-flow-components/schemas';

export type EditorMode = 'form' | 'raw';

export interface ComponentVersionHistoryEntry {
  version: string;
  recorded: string;
  json: string;
}

export interface ComponentJsonEditorProps {
  folder: CareFlowFolder;
  component: CareFlowComponentJson;
  history?: ReadonlyArray<ComponentVersionHistoryEntry>;
  onSave: (candidate: unknown) => void;
  onCancel: () => void;
  blockOnSharedConfirm?: boolean;
  onAcceptSharedWarning?: () => void;
  className?: string;
}

function naiveLineDiff(before: string, after: string): string {
  const beforeLines = before.split('\n');
  const afterLines = after.split('\n');
  const max = Math.max(beforeLines.length, afterLines.length);
  const out: string[] = [];
  for (let i = 0; i < max; i++) {
    const b = beforeLines[i];
    const a = afterLines[i];
    if (b === a) {
      out.push(`  ${b ?? ''}`);
    } else {
      if (b !== undefined) out.push(`- ${b}`);
      if (a !== undefined) out.push(`+ ${a}`);
    }
  }
  return out.join('\n');
}

export function ComponentJsonEditor({
  folder,
  component,
  history = [],
  onSave,
  onCancel,
  blockOnSharedConfirm = false,
  onAcceptSharedWarning,
  className,
}: ComponentJsonEditorProps) {
  const initialJson = useMemo(
    () => JSON.stringify(component, null, 2),
    [component],
  );
  const [editorMode, setEditorMode] = useState<EditorMode>('form');
  const [rawJson, setRawJson] = useState<string>(initialJson);
  const [labelDraft, setLabelDraft] = useState<string>(component.label);
  const [versionDraft, setVersionDraft] = useState<string>(component.version);
  const [validation, setValidation] = useState<ValidationResult<unknown> | null>(
    null,
  );

  const shared = isSharedFolder(folder);
  const showSharedWarning = shared && blockOnSharedConfirm;

  const buildCandidateFromForm = (): unknown => {
    return {
      ...component,
      label: labelDraft,
      version: versionDraft,
    };
  };

  const handleSave = () => {
    let candidate: unknown;
    if (editorMode === 'form') {
      candidate = buildCandidateFromForm();
    } else {
      try {
        candidate = JSON.parse(rawJson);
      } catch (err) {
        setValidation({
          ok: false,
          errors: [
            {
              path: '$',
              message: `JSON parse error: ${(err as Error).message}`,
            },
          ],
        });
        return;
      }
    }
    const result = validateComponentJson(folder, candidate);
    setValidation(result);
    if (result.ok) {
      onSave(result.value);
    }
  };

  return (
    <section
      aria-labelledby={`care-flow-component-editor-title-${component.id}`}
      data-testid="care-flow-component-editor"
      data-folder={folder}
      data-component-id={component.id}
      className={[
        'flex w-full flex-col gap-gutter-md p-gutter-lg',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="flex items-center justify-between">
        <div>
          <h3
            id={`care-flow-component-editor-title-${component.id}`}
            className="text-signal-lg font-semibold text-text-primary"
          >
            {CARE_FLOW_FOLDER_LABELS[folder]}: {component.label}
          </h3>
          <p className="mt-1 font-mono text-signal-xs text-text-tertiary">
            {component.id}
          </p>
        </div>
        <div
          role="radiogroup"
          aria-label="Editor mode"
          data-testid="care-flow-component-editor-mode-toggle"
          className="flex rounded-signal-md border border-surface-border bg-surface-base p-1"
        >
          <button
            type="button"
            role="radio"
            aria-checked={editorMode === 'form'}
            data-testid="care-flow-component-editor-mode-form"
            onClick={() => setEditorMode('form')}
            className={[
              'rounded-signal-sm px-gutter-md py-1 text-signal-sm focus:outline-none focus:ring-2 focus:ring-signal-primary-500',
              editorMode === 'form'
                ? 'bg-signal-primary-500 text-text-inverse'
                : 'text-text-secondary',
            ].join(' ')}
          >
            Form
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={editorMode === 'raw'}
            data-testid="care-flow-component-editor-mode-raw"
            onClick={() => setEditorMode('raw')}
            className={[
              'rounded-signal-sm px-gutter-md py-1 text-signal-sm focus:outline-none focus:ring-2 focus:ring-signal-primary-500',
              editorMode === 'raw'
                ? 'bg-signal-primary-500 text-text-inverse'
                : 'text-text-secondary',
            ].join(' ')}
          >
            Raw JSON
          </button>
        </div>
      </header>

      {showSharedWarning ? (
        <div
          role="alertdialog"
          aria-labelledby={`shared-warning-title-${component.id}`}
          data-testid="care-flow-component-editor-shared-warning"
          className="rounded-signal-md border border-signal-primary-500 bg-surface-muted p-gutter-md text-signal-sm text-text-primary"
        >
          <h4
            id={`shared-warning-title-${component.id}`}
            className="font-semibold"
          >
            Shared component
          </h4>
          <p className="mt-1 text-text-secondary">
            This {CARE_FLOW_FOLDER_LABELS[folder].toLowerCase()} is used by{' '}
            {component.usedByPathwayIds?.length ?? 0} pathways. Editing
            affects all of them. Continue?
          </p>
          <div className="mt-gutter-sm flex gap-gutter-sm">
            <button
              type="button"
              data-testid="care-flow-component-editor-shared-warning-accept"
              onClick={() => onAcceptSharedWarning?.()}
              className="rounded-signal-md bg-signal-primary-500 px-gutter-md py-1 text-signal-sm font-semibold text-text-inverse focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            >
              Continue
            </button>
            <button
              type="button"
              data-testid="care-flow-component-editor-shared-warning-cancel"
              onClick={onCancel}
              className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-md py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {editorMode === 'form' ? (
        <fieldset
          data-testid="care-flow-component-editor-form"
          disabled={showSharedWarning}
          className="flex flex-col gap-gutter-sm rounded-signal-md border border-surface-border bg-surface-base p-gutter-md"
        >
          <legend className="px-1 text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary">
            Form mode
          </legend>
          <label
            htmlFor={`component-label-${component.id}`}
            className="text-signal-xs font-semibold text-text-secondary"
          >
            Label
          </label>
          <input
            id={`component-label-${component.id}`}
            data-testid="care-flow-component-editor-input-label"
            type="text"
            value={labelDraft}
            onChange={(e) => setLabelDraft(e.target.value)}
            className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
          />
          <label
            htmlFor={`component-version-${component.id}`}
            className="text-signal-xs font-semibold text-text-secondary"
          >
            Version
          </label>
          <input
            id={`component-version-${component.id}`}
            data-testid="care-flow-component-editor-input-version"
            type="text"
            value={versionDraft}
            onChange={(e) => setVersionDraft(e.target.value)}
            className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
          />
          <p className="text-signal-xs text-text-tertiary">
            Switch to Raw JSON to edit folder-specific fields directly.
          </p>
        </fieldset>
      ) : (
        <div className="flex flex-col gap-gutter-sm rounded-signal-md border border-surface-border bg-surface-base p-gutter-md">
          <label
            htmlFor={`component-raw-json-${component.id}`}
            className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
          >
            Raw JSON
          </label>
          <textarea
            id={`component-raw-json-${component.id}`}
            data-testid="care-flow-component-editor-input-raw"
            value={rawJson}
            onChange={(e) => setRawJson(e.target.value)}
            disabled={showSharedWarning}
            spellCheck={false}
            rows={16}
            className="rounded-signal-md border border-surface-border bg-surface-base p-gutter-sm font-mono text-signal-xs text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
          />
        </div>
      )}

      {validation && !validation.ok ? (
        <div
          role="alert"
          data-testid="care-flow-component-editor-validation-errors"
          className="rounded-signal-md border border-signal-danger-500 bg-surface-base p-gutter-md text-signal-sm text-signal-danger-500"
        >
          <p className="font-semibold">
            {validation.errors.length}{' '}
            {validation.errors.length === 1 ? 'error' : 'errors'} need
            fixing
          </p>
          <ul className="mt-1 list-disc pl-gutter-md">
            {validation.errors.map((err, i) => (
              <li
                key={`${err.path}-${i}`}
                data-testid={`care-flow-component-editor-validation-error-${i}`}
              >
                <span className="font-mono">{err.path}</span>: {err.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {validation && validation.ok ? (
        <p
          role="status"
          data-testid="care-flow-component-editor-validation-ok"
          className="rounded-signal-md border border-signal-success-500 bg-surface-base p-gutter-sm text-signal-sm text-signal-success-500"
        >
          Valid. Saved.
        </p>
      ) : null}

      <div className="flex items-center gap-gutter-sm">
        <button
          type="button"
          data-testid="care-flow-component-editor-save"
          onClick={handleSave}
          disabled={showSharedWarning}
          className="rounded-signal-md bg-signal-primary-500 px-gutter-md py-1 text-signal-sm font-semibold text-text-inverse focus:outline-none focus:ring-2 focus:ring-signal-primary-500 disabled:opacity-60"
        >
          Save
        </button>
        <button
          type="button"
          data-testid="care-flow-component-editor-cancel"
          onClick={onCancel}
          className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-md py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
        >
          Cancel
        </button>
      </div>

      {history.length > 0 ? (
        <aside
          aria-labelledby={`component-history-title-${component.id}`}
          data-testid="care-flow-component-editor-history"
          className="mt-gutter-md flex flex-col gap-gutter-sm rounded-signal-md border border-surface-border bg-surface-base p-gutter-md"
        >
          <h4
            id={`component-history-title-${component.id}`}
            className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
          >
            Version history
          </h4>
          <ul role="list" className="flex flex-col gap-gutter-sm">
            {history.map((entry, i) => {
              const next = history[i + 1] ?? { json: initialJson };
              const diff = naiveLineDiff(entry.json, next.json);
              return (
                <li
                  key={entry.version}
                  data-testid={`care-flow-component-editor-history-entry-${entry.version}`}
                  className="flex flex-col gap-1"
                >
                  <span className="font-mono text-signal-xs text-text-secondary">
                    {entry.version} - {entry.recorded}
                  </span>
                  <pre className="overflow-x-auto rounded-signal-sm bg-surface-muted p-gutter-sm font-mono text-signal-xs text-text-secondary">
                    {diff}
                  </pre>
                </li>
              );
            })}
          </ul>
        </aside>
      ) : null}
    </section>
  );
}
